/**
 * Type-safe payload extraction helpers for WebSocket events.
 *
 * Ably msgpack may deliver values as unexpected types (e.g. number as string).
 * These helpers handle all common type variations safely, preventing silent failures
 * that break render progress, job transitions, and other event-driven flows.
 */

type Payload = Record<string, unknown> | undefined;

/** Extract string — converts any non-null value via String() */
export function payloadString(
  payload: Payload,
  key: string
): string | undefined {
  const value = payload?.[key];
  if (value == null) return undefined;
  return typeof value === 'string' ? value : String(value);
}

/** Extract number — handles number, string, boolean */
export function payloadNumber(
  payload: Payload,
  key: string
): number | undefined {
  const value = payload?.[key];
  if (value == null) return undefined;
  if (typeof value === 'number') return isNaN(value) ? undefined : value;
  if (typeof value === 'string') {
    const n = Number(value);
    return isNaN(n) ? undefined : n;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  return undefined;
}

/** Extract boolean — handles boolean, number (0/1), string ("true"/"false") */
export function payloadBool(
  payload: Payload,
  key: string
): boolean | undefined {
  const value = payload?.[key];
  if (value == null) return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return value === 'true' || value === '1';
  return undefined;
}

/** Extract array of objects */
export function payloadArray(
  payload: Payload,
  key: string
): Record<string, unknown>[] | undefined {
  const value = payload?.[key];
  return Array.isArray(value)
    ? (value as Record<string, unknown>[])
    : undefined;
}

/** Extract nested object */
export function payloadObject(
  payload: Payload,
  key: string
): Record<string, unknown> | undefined {
  const value = payload?.[key];
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/** Shorthand: extract string with fallback */
export function pStr(payload: Payload, key: string, fallback: string): string {
  return payloadString(payload, key) ?? fallback;
}

/** Shorthand: extract number with fallback */
export function pNum(payload: Payload, key: string, fallback: number): number {
  return payloadNumber(payload, key) ?? fallback;
}
